import {
  LINK_DOCUSIGN_TCA_CIEEMG_FALLBACK,
  ANEXO_ID_SUFFIXES_TO_STRIP,
  STATUS_CORES,
} from "../../constants/contratos.js";
import {
  calcularSalarioMinimoPorCargaHoraria,
  buildMensagemSalarioInferiorMinimo,
  validarSalarioMinimo,
  getLimiteHorasAprendizagem,
} from "../../utils/aprendizagem.js";
import {
  formatDateBR,
  formatarCnpjCpf,
  isTelefonePlaceholder,
  sanitizeTelefone,
  formatHorarioTeoricoLabel,
  parseSalario,
  calcularIdade,
  isMenorDeIdade,
} from "../../utils/formatters.js";

export {
  LINK_DOCUSIGN_TCA_CIEEMG_FALLBACK,
  ANEXO_ID_SUFFIXES_TO_STRIP,
  STATUS_CORES,
  calcularSalarioMinimoPorCargaHoraria,
  buildMensagemSalarioInferiorMinimo,
  validarSalarioMinimo,
  getLimiteHorasAprendizagem,
  formatDateBR,
  formatarCnpjCpf,
  isTelefonePlaceholder,
  sanitizeTelefone,
  formatHorarioTeoricoLabel,
  parseSalario,
  calcularIdade,
  isMenorDeIdade,
};

export const CAMPOS_FILIAL_TCA_COMUM = [
  "nome_filial",
  "cpfCnpj",
  "cepFilial",
  "enderecoFilial",
  "telefones",
  "telefones2",
  "bairro",
  "municipio",
  "uf",
  "emailContratos",
  "emailFinanceiro",
  "representadaPor",
  "cargoRepresentante",
  "emailRepresentante",
  "responsavelSupervisor",
  "cargoSupervisor",
  "emailSupervisor",
];

export const ANEXOS_SCHEMA_TCA_COMUM = [
  {
    id: "anexo_comprovante_endereco",
    label: "Comprovante de Endereço",
    icon: "Home",
    required: true,
  },
  {
    id: "anexo_historico_conclusao",
    label: "Declaração escolar e/ou histórico de conclusão do Ensino Médio",
    hint: "Até 30 dias de emissão",
    icon: "GraduationCap",
    required: true,
  },
  {
    id: "anexo_documento_identificacao",
    label: "Documento de Identificação (RG ou CNH)",
    icon: "IdCard",
    required: true,
  },
];

/**
 * Formata um objeto de endereço para linha única legível.
 *
 * @param {object|string} dados - Objeto com campos de endereço ou string
 * @returns {string} endereço formatado
 */
export function formatarEnderecoLimpo(dados) {
  if (!dados) return "";
  if (typeof dados === "string") return dados.trim();

  const endPronto = String(dados.endereco || "").trim();
  if (endPronto && (endPronto.includes(",") || endPronto.includes("-"))) {
    return endPronto;
  }

  const logradouro = String(dados.logradouro || dados.endereco || "").trim();
  const numero = String(dados.numero || dados.numero_endereco || "").trim();
  const complemento = String(dados.complemento || "").trim();
  const bairro = String(dados.bairro || "").trim();
  const cidade = String(dados.cidade || dados.municipio || "").trim();
  const uf = String(dados.uf || dados.estado || dados.estado_endereco || "").trim();

  let linha = logradouro;
  if (numero) linha = linha ? `${linha}, ${numero}` : numero;
  if (complemento) linha = linha ? `${linha} - ${complemento}` : complemento;
  if (bairro) linha = linha ? `${linha} - ${bairro}` : bairro;
  if (cidade && uf) linha = linha ? `${linha} - ${cidade}/${uf}` : `${cidade}/${uf}`;
  else if (cidade) linha = linha ? `${linha} - ${cidade}` : cidade;

  if (dados.cep && !linha.includes(dados.cep)) {
    const cepStr = `CEP: ${dados.cep}`;
    linha = linha ? `${linha}, ${cepStr}` : cepStr;
  }

  return linha.trim();
}

/**
 * Normaliza o ID de um campo pendente removendo sufixos conhecidos de anexo.
 *
 * @param {string} id - Chave do campo/anexo
 * @returns {string} id normalizado
 */
export function normalizeMissingItemId(id) {
  if (typeof id !== "string") return id;
  for (const suffix of ANEXO_ID_SUFFIXES_TO_STRIP) {
    if (id.endsWith(suffix)) return id.slice(0, -suffix.length);
  }
  return id;
}

/**
 * Remove duplicatas de uma lista de itens faltantes preservando a ordem.
 *
 * @param {Array<{ id: string, label: string }>} items
 * @returns {Array<{ id: string, label: string }>}
 */
export function dedupeMissingItems(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    if (!item || item.id == null) return;
    if (seen.has(item.id)) return;
    seen.add(item.id);
    result.push(item);
  });
  return result;
}

/**
 * Converte entrada/saída (HH:MM) em horas decimais considerando intervalo.
 *
 * @param {string} entrada - Hora de entrada
 * @param {string} saida - Hora de saída
 * @param {string} [intervaloInicio] - Início do intervalo
 * @param {string} [intervaloFim] - Fim do intervalo
 * @returns {number}
 */
export function horasDecimais(
  entrada,
  saida,
  intervaloInicio = "",
  intervaloFim = "",
) {
  if (!entrada || !saida) return 0;
  const [eh, em] = entrada.split(":").map(Number);
  const [sh, sm] = saida.split(":").map(Number);
  let totalMin = sh * 60 + sm - (eh * 60 + em);
  if (totalMin < 0) totalMin += 24 * 60;
  if (intervaloInicio && intervaloFim) {
    const [ih, im] = intervaloInicio.split(":").map(Number);
    const [fh, fm] = intervaloFim.split(":").map(Number);
    let intervaloMin = fh * 60 + fm - (ih * 60 + im);
    if (intervaloMin < 0) intervaloMin += 24 * 60;
    totalMin -= intervaloMin;
  }
  return totalMin / 60;
}

/**
 * Valida a carga horária de horários do TCA.
 *
 * @param {object} horarioObj - Estrutura { tipo, diasSelecionados, fixo, alternado }
 * @returns {{ errors: string[], totalSemanal: number }}
 */
export function validarHorarios(horarioObj) {
  const errors = [];
  let totalSemanal = 0;
  if (horarioObj.tipo === "fixo") {
    const f = horarioObj.fixo;
    if (f.entrada && f.saida) {
      const horas = horasDecimais(
        f.entrada,
        f.saida,
        f.intervaloInicio,
        f.intervaloFim,
      );
      if (horas > 6)
        errors.push("Carga horária diária não pode ultrapassar 6h");
      if (horas >= 6 && (!f.intervaloInicio || !f.intervaloFim)) {
        errors.push("Intervalo obrigatório para jornada de 6h");
      }
      totalSemanal = horas * horarioObj.diasSelecionados.length;
    }
  } else if (horarioObj.tipo === "alternado") {
    const diasMap = {
      segunda: "Segunda",
      terca: "Terça",
      quarta: "Quarta",
      quinta: "Quinta",
      sexta: "Sexta",
    };
    Object.keys(horarioObj.alternado).forEach((key) => {
      const h = horarioObj.alternado[key];
      if (h.entrada && h.saida) {
        const horas = horasDecimais(
          h.entrada,
          h.saida,
          h.intervaloInicio,
          h.intervaloFim,
        );
        if (horas > 6)
          errors.push(`${diasMap[key]}: carga horária superior a 6h`);
        totalSemanal += horas;
      }
    });
  }
  if (totalSemanal > 30)
    errors.push(
      `Carga horária semanal (${totalSemanal.toFixed(1)}h) superior ao limite de 30h`,
    );
  return { errors, totalSemanal };
}
